import {
  FormTab,
  Group,
  GroupItem,
  GroupTitle,
  Toolbar,
  TransactionLogsField,
  TransactionNotesInput,
  TransactionNotesIsPrivateInput,
  useSaveMutation,
} from "ra-friendsofbabba";
import {
  Input,
  ProjectProposalInput,
  TechnicalReportGroupInput,
  UniversityDepartmentInput,
  UserResearcherInput,
} from "../component/input";
import React, { useMemo } from "react";
import {
  TabbedForm,
  required,
  useNotify,
  usePermissions,
  useRedirect,
  useRefresh,
  useTranslate,
} from "react-admin";

import ActivitiesField from "./fields/ActivitiesField";
import CollaborationsField from "./fields/CollaborationsField";
import ConferencesField from "./fields/ConferencesField";
import { DRAWER_WIDTH } from "../config";
import DeliverablesField from "./fields/DeliverablesField";
import EnhancementActionsField from "./fields/EnhancementActionsField";
import HelpAlert from "./HelpAlert";
import PatentsField from "./fields/PatentsField";
import PrintButton from "../component/button/PrintButton";
import ProjectsField from "./fields/ProjectsField";
import RepliesTab from "./tabs/RepliesTab";
import ScientificProductionsField from "./fields/ScientificProductionsField";
import Section4Tab from "./tabs/Section4Tab";
import Section5Tab from "./tabs/Section5Tab";
import SignedDocTab from "./tabs/SignedDocTab";
import VerificationMeetTab from "./tabs/VerificationMeetTab";
import { get } from "lodash";
import { makeStyles } from "@material-ui/core";
import { useSelector } from "react-redux";

const useStyles = makeStyles((theme) => ({
  root: ({ sidebarOpen, sidebarWidth }) => ({
    "& .MuiTabs-scroller.MuiTabs-fixed": {
      overflowX: "auto !important",
      [theme.breakpoints.up("md")]: {
        maxWidth: sidebarOpen
          ? `calc(100vw - ${sidebarWidth + 64}px)`
          : "calc(100vw - 65px)",
      },
      [theme.breakpoints.down("sm")]: {
        maxWidth: sidebarOpen
          ? `calc(100vw - ${sidebarWidth + 64}px)`
          : "calc(100vw - 65px)",
      },
      [theme.breakpoints.down("xs")]: {
        maxWidth: "calc(100vw - 17px)",
      },
    },
  }),
  help: {
    marginBottom: theme.spacing(2),
  },
}));

const Form = (props) => {
  const { loaded, permissions } = usePermissions();
  const isAdmin = useMemo(
    () => loaded && permissions(["admin"]),
    [loaded, permissions]
  );
  const referenceYear = useMemo(
    () => get(props, "record.reference_year", null),
    [props]
  );
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const redirect = useRedirect();
  const save = useSaveMutation({
    ...props,
    onSuccess: (response, values) => {
      const prevState = get(values, "transaction.state");
      const nextState = get(response, "data.state");
      const isMoved = prevState !== nextState;
      notify(
        `resources.technical-reports.messages.${values.state}_${
          isMoved ? "moved" : "loop"
        }.save_success`
      );
      if (values.id > 0) {
        refresh();
      } else {
        redirect("edit", props.basePath, response.data.id);
      }
    },
  });
  const sidebarOpen = useSelector((state) => state?.admin?.ui?.sidebarOpen);
  const sidebarWidth = DRAWER_WIDTH;
  const classes = useStyles({ sidebarOpen, sidebarWidth });
  const reportType = get(props, "record.technical_report_group_id");
  const state = get(props, "record.transaction.state");

  return (
    <TabbedForm
      {...props}
      save={save}
      className={classes.root}
      initialValues={{ is_private: false }}
      toolbar={
        <Toolbar useWorkflow maxButtonsToDisplay={1}>
          <PrintButton />
        </Toolbar>
      }
    >
      <FormTab label="resources.technical-reports.tabs.the_project">
        <Input
          source="executive_project.project_proposal_id"
          project_proposal="executive_project.project_proposal"
          validate={required()}
          fullWidth
          component={<ProjectProposalInput />}
        />
        <Group fullWidth>
          <GroupItem lg={7} md={4} sm={6}>
            <UniversityDepartmentInput
              source="user.profile.university_department_id"
              disabled
            />
          </GroupItem>
          <GroupItem lg={7} md={4} sm={6}>
            <Input
              source="technical_report_group_id"
              validate={required()}
              component={<TechnicalReportGroupInput />}
            />
          </GroupItem>
          <GroupItem lg={7} md={4} sm={6}>
            <Input
              source="user_id"
              fullWidth
              component={<UserResearcherInput />}
            />
          </GroupItem>
        </Group>

        <TransactionLogsField admin={isAdmin} source="logs" fullWidth />
        {reportType > 2 && <HelpAlert />}
        {isAdmin && (
          <TransactionNotesInput
            source="notes"
            helperText="resources.transactions.fields.notes.help"
            fullWidth
          />
        )}
        {isAdmin && (
          <TransactionNotesIsPrivateInput
            admin={isAdmin}
            source="is_private"
            helperText="resources.transactions.fields.is_private.help"
            fullWidth
          />
        )}
      </FormTab>

      <FormTab label="resources.technical-reports.tabs.section_1">
        <Group wrapper>
          <GroupTitle
            title={translate("resources.technical-reports.title.section_1")}
            subTitle={translate("resources.technical-reports.help.section_1", {
              referenceYear,
            })}
          />
        </Group>
        <Input source="activities" fullWidth component={<ActivitiesField />} />
        <Input
          source="deliverables"
          fullWidth
          component={<DeliverablesField />}
        />
        {reportType > 2 && <HelpAlert />}
      </FormTab>

      <FormTab label="resources.technical-reports.tabs.section_2">
        <Group wrapper>
          <GroupTitle
            title={translate("resources.technical-reports.title.section_2")}
          />
        </Group>
        <Input
          source="collaborations"
          fullWidth
          component={<CollaborationsField referenceYear={referenceYear} />}
        />
        {reportType > 2 && <HelpAlert />}
      </FormTab>

      <FormTab label="resources.technical-reports.tabs.section_3">
        <Group wrapper>
          <GroupTitle
            title={translate("resources.technical-reports.title.section_3")}
            subTitle={translate("resources.technical-reports.help.section_3", {
              referenceYear,
            })}
          />
        </Group>
        <Input
          source="conferences"
          fullWidth
          component={<ConferencesField />}
        />
        <Input source="projects" fullWidth component={<ProjectsField />} />
        <Input source="patents" fullWidth component={<PatentsField />} />
        <Input
          source="scientific_productions"
          fullWidth
          referenceYear={referenceYear}
          component={<ScientificProductionsField />}
        />
        <Input
          source="enhancement_actions"
          fullWidth
          component={<EnhancementActionsField />}
        />
        {reportType > 2 && <HelpAlert />}
      </FormTab>
      <Section4Tab reportType={reportType} />
      {reportType > 2 && <Section5Tab reportType={reportType} />}
      {reportType === 2 && <VerificationMeetTab />}
      {reportType > 2 && state !== "draft" && <SignedDocTab />}
      {reportType > 1 && <RepliesTab />}
    </TabbedForm>
  );
};
export default Form;
